import type { IBundleOptionInput, Product } from "@jti/magento";

import type { UserActionTrigger } from "./userActionTriggerIntent";

/**
 * Allowed GTM event names for the message bus tracking channel.
 * Must stay in sync with the listener registrations in BaseGTMRunner.trackCustomEvents()
 * and BaseGTMRunner.trackMessageBusEvents() in @jti/tracker.
 */
export type GTMEventName =
  // Commerce
  | "purchase"
  | "add_payment_info"
  | "add_shipping_info"
  | "begin_checkout"
  | "checkout_progress"
  | "view_cart"
  | "add_to_cart"
  | "remove_from_cart"
  | "view_item_list"
  | "select_item"
  | "view_item"
  | "re_order"
  | "pause_subscription"
  | "resume_subscription"
  | "stop_subscription"
  | "add_to_subscription"
  | "remove_item"
  | "increase_carton"
  | "decrease_carton"
  // Non-commerce
  | "filter_interaction"
  | "device_register"
  | "device_deregister"
  | "cta_click"
  | "cta_click_payment"
  | "faq_click"
  | "faq_search"
  | "faq_search_results_click"
  | "share_click"
  | "copy_a_code"
  | "referral_code_inserted"
  | "points_history_click"
  | "pack_code_insertion"
  | "list_click"
  | "login"
  | "login_fail"
  | "login_click"
  | "kakao_login"
  | "create_account_click"
  | "sign_out"
  | "contact_form_submit"
  | "unsubscribe"
  | "free_trial_start"
  | "sign_up"
  | "sign_up_progress"
  | "redeem_click"
  | "reward_discover_more_click"
  | "redeem_checkout"
  | "page_view"
  | "flavor_finder_progress"
  | "flavor_finder_result"
  | "video_click"
  | "video_stop"
  | "hav_start"
  | "hav_success"
  | "follow"
  | "contact_click"
  | "phone_click"
  | "article_click"
  | "store_locator_click"
  | "store_locator_search"
  | "store_locator_search_results_click"
  | "newsletter_subscription"
  | "menu_navigation"
  | "click_carousel"
  | "age_verification"
  | "survey_start"
  | "survey_progress"
  | "survey_completion"
  | "popin_close"
  | "popin_link_click"
  | "popin_view"
  | "quiz_start"
  | "quiz_end"
  | "search"
  | "search_result_click"
  | "cross_sell_popup_impression";

/**
 * General rule with creating new topic is to avoid naming that is not separated by coma or previously defined `channels` are contained it new name.
 * This is because of the way how `IMessageBus` is implemented. And how `PickStartsWith` filters expected types for subscribe method.
 *
 * For example:
 * - `cart.add` is a valid name
 * - `cart.add_to_cart` is a not valid name because `cart.add` is already defined
 * - `cart.add.to.cart` is a not valid name because `cart.add` is already defined
 * - `cart.pushToCart` is a valid name
 */
export type Signatures = {
  // signatures required for testing ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓
  "test.message": { test: string };
  "test.info": { test: string };
  // ↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑
  "tracking.event": {
    event_name: GTMEventName;
    additionalData?: { [key: string]: string | number | boolean | object };
  };
  "cart.loaded": { cartId: string };
  "cart.add": {
    sku: string;
    bundle: IBundleOptionInput[];
    quantity: number;
    isFreeTrial?: boolean;
    isConsentGiven?: boolean;
    requestId?: string;
  };
  "cart.limit.update": { sku: string; quantity: number; limit: number; quantityLimitReached: boolean };
  "cart.fetched.state": { fetched: boolean };
  "success.cart.add": { message?: string; requestId?: string };
  "fail.cart.add": { message: string; requestId?: string };
  "drawer.open": null;
  "drawer.close": null;
  "drawer.toggle": { isOpen: boolean };
  "search.value": { searchValue: string };
  "search.total.results": { searchTotalResults: number; isResultsLoading: boolean };
  "update.breadcrumb.name": { breadcrumbName: string };
  "infobanner.active": { infoBannerHeight: number };
  "infobanner.close": null;
  "cart.items.fetched": { items: Product[] };
  "user.logout": null;
  "user.auth.root.navigation": null;
  "welcome.popup.state": { isOpen: boolean };
  "province.popup.state": { isOpen: boolean };
  "user.action.trigger": { trigger: UserActionTrigger };
};

export type PublishEnvelope<PayloadRecord, Topic extends keyof PayloadRecord = keyof PayloadRecord> = {
  headers?: Record<string, string>;
  payload: PayloadRecord[Topic];
};

export interface Envelope<
  PayloadRecord = Signatures,
  Topic extends keyof PayloadRecord = keyof PayloadRecord,
> extends PublishEnvelope<PayloadRecord, Topic> {
  channel: Topic;
  timestamp: number;
}

export type PickStartsWith<T, S extends string> = {
  [K in keyof T as K extends `${S}${infer _R}` ? K : never]: T[K];
};

export type MessageBusHistory = {
  [T in keyof Signatures]?: Envelope<Signatures, T>;
};

export type PublishOptions = {
  sticky: boolean;
};

export interface IMessageBus {
  subscribe<
    Channel extends keyof Signatures,
    PayloadRecord extends (Channel extends "*" ? Signatures : PickStartsWith<Signatures, Channel>),
  >(request: {
    channel: Channel;
    callback(response: Envelope<PayloadRecord>): void;
  }): string;

  unsubscribe<Channel extends keyof Signatures>(channel: Channel): string | boolean;
  unsubscribeToken(token: string): string | boolean;

  publish<Channel extends keyof Signatures>(
    channel: Channel,
    envelop: PublishEnvelope<Signatures, Channel>,
    options?: PublishOptions
  ): void;

  getHistory(): MessageBusHistory;
  removeHistoryItem<Topic extends keyof Signatures>(channel: Topic): void;
  getLastHistory<Topic extends keyof Signatures>(channel: Topic): Envelope<Signatures, Topic> | undefined;
  reset(): void;
}
